import React, { useCallback, useEffect, useState } from 'react';
import TariffChargesTable from './TariffChargesTable';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const COLORS = {
  panel: '#1e293b',
  border: '#334155',
  text: '#e2e8f0',
  textDim: '#94a3b8',
  accent: '#4ecca3',
};

const TariffDetail = ({ tariffId }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!tariffId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(`${API}/api/grid-tariffs/${tariffId}`)
      .then((r) => r.json())
      .then((d) => setDetail(d))
      .catch((e) => setError('Failed to load tariff: ' + e.message))
      .finally(() => setLoading(false));
  }, [tariffId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!tariffId) {
    return (
      <div style={{
        flex: 1,
        padding: '40px',
        color: COLORS.textDim,
        fontSize: '14px',
        fontFamily: "'Work Sans', sans-serif",
      }}>
        Select a tariff from the list.
      </div>
    );
  }

  if (loading && !detail) {
    return <div style={{ padding: '20px', color: COLORS.textDim }}>Loading…</div>;
  }

  if (error) {
    return <div style={{ padding: '20px', color: '#fecaca' }}>{error}</div>;
  }

  if (!detail) return null;

  const t = detail.tariff;

  const metaRow = (label, value) => (
    <div style={{ display: 'flex', fontSize: '12px', marginBottom: '4px' }}>
      <div style={{
        width: '130px',
        color: COLORS.textDim,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        fontSize: '10px',
        paddingTop: '2px',
      }}>
        {label}
      </div>
      <div style={{ color: COLORS.text, fontSize: '12px' }}>{value || '—'}</div>
    </div>
  );

  return (
    <div style={{
      flex: 1,
      minWidth: 0,
      fontFamily: "'Work Sans', sans-serif",
    }}>
      {/* Metadata header */}
      <div style={{
        background: COLORS.panel,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '6px',
        padding: '16px 20px',
        marginBottom: '16px',
      }}>
        <div style={{
          fontSize: '18px',
          fontWeight: 600,
          color: COLORS.text,
          fontFamily: "'Space Grotesk', sans-serif",
          marginBottom: '2px',
        }}>
          {t.name}
        </div>
        <div style={{
          fontSize: '12px',
          color: COLORS.textDim,
          marginBottom: '12px',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {t.utility_code} / {t.code} · {t.direction}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '6px 24px',
        }}>
          {metaRow('Utility', t.utility_name)}
          {metaRow('Country', t.country_code)}
          {metaRow('Customer type', t.customer_type)}
          {metaRow('Currency', t.currency)}
          {metaRow('Valid from', t.valid_from)}
          {metaRow('Valid to', t.valid_to)}
          {metaRow('High season', `${t.season_high_start} → ${t.season_high_end}`)}
          {metaRow('TOU schedule', `#${t.tou_schedule_id}`)}
        </div>

        {t.notes && (
          <div style={{
            marginTop: '10px',
            paddingTop: '10px',
            borderTop: `1px solid ${COLORS.border}`,
            fontSize: '12px',
            color: COLORS.textDim,
            fontStyle: 'italic',
          }}>
            {t.notes}
          </div>
        )}
        {t.source_document && (
          <div style={{
            marginTop: '6px',
            fontSize: '11px',
            color: COLORS.textDim,
          }}>
            Source: {t.source_document}
          </div>
        )}
      </div>

      {/* Charges table */}
      <TariffChargesTable
        tariffId={tariffId}
        charges={detail.charges || []}
        onSaved={load}
      />
    </div>
  );
};

export default TariffDetail;
